import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, CheckCircle2, MessagesSquare, PlugZap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  CHANNEL_TILE_CLASS,
  ChannelIcon,
  channelBandStyle,
  useChannelStats,
} from '@/pages/integrations/channel-catalog'

/**
 * Màn chào lần đầu của Nhắn tin: chưa có kênh nào, chưa có hội thoại nào.
 * Lấy từ SaleSmartly bản cũ (khối "Kết nối nhiều kênh hơn" ngay trong khung
 * chat, kèm lối "chat thử") và bản mới (thẻ kênh gọn, báo đã kết nối hay chưa).
 */
export function ConnectChannelsOnboarding() {
  const { t } = useTranslation('convList')
  const { stats, channels } = useChannelStats()

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-8 py-12">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <MessagesSquare className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h2 className="text-xl font-medium">{t('onboarding.title')}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('onboarding.intro')}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button asChild>
                <Link to="/integrations">
                  <PlugZap /> {t('onboarding.openIntegrations')}
                </Link>
              </Button>
              {/* Thay cho "phiên giả lập" của SaleSmartly: tạo Live Chat rồi tự
                  nhắn thử ở khung xem trước — tin vào thẳng hộp thư này. */}
              <Button asChild variant="secondary">
                <Link to="/integrations/website?add=1">{t('onboarding.tryLiveChat')}</Link>
              </Button>
            </div>
          </div>
        </div>

        <div className="my-8 border-t" />

        <h3 className="text-base font-medium">{t('onboarding.connectTitle')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('onboarding.connectHint')}
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {channels.filter((c) => !c.beta).map((c) => {
            const done = (stats[c.key]?.total ?? 0) > 0
            return (
              <Link
                key={c.key}
                to={`/integrations/${c.key}${done ? '' : '?add=1'}`}
                className={cn(CHANNEL_TILE_CLASS, 'group')}
                // Cùng dải màu thương hiệu với thẻ kênh ở trang Tích hợp (channel-catalog).
                style={channelBandStyle(c)}
              >
                <span className="flex items-center gap-2.5">
                  <ChannelIcon def={c} />
                  <span className="font-medium">{c.name}</span>
                </span>
                <span className="mt-2 line-clamp-2 flex-1 text-xs leading-5 text-muted-foreground">
                  {c.description}
                </span>
                <span className="mt-3 flex items-center gap-3 text-xs">
                  {done && (
                    <span className="flex items-center gap-1 text-success">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {t('onboarding.connected')}
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-primary group-hover:underline">
                    {done ? t('onboarding.manage') : t('onboarding.connect')} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </span>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}
